/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Box, createTheme, Divider, IconButton, ListItemIcon, ListSubheader, MenuList, Stack, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { UserStateActionTypeEnum, useUserContext } from '../../contexts/user-context/UserContext';
import { LoginChangeCustomerStorageKey, RedirectFromLogoutStorageKey } from '../../views/login-view/LoginViewUtils';
import { UIViewEnum } from '../../common/enums/UIViewEnum';
import { routesByViewID } from '../../routes/routes';
import storageService from '../../services/storage/storage-service/StorageService';
import useNavigateTo from '../../hooks/use-navigate-to/useNavigateTo';
import Enumerable from 'linq';
import { appHeaderActionItems } from './AppHeaderActions';
import { ActionMenuItemCategoryEnum } from '../action-menu/BaseActions';
import { IKeyValuePair } from '../../common/models/KeyValuePair';
import { ActionMenuItem, BaseActionEnum } from '../action-menu/ActionMenuItem';
import { isMobile, isTablet } from 'react-device-detect';
import { useAppSettings } from '../../contexts/app-context/AppContext';
import appSettingsJson from "../../app-settings.json";
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import UserDataService from '../../services/user-data-service/UserDataService';
import eLynxAuthService from '../../services/authentication/elynx-auth-service/ELynxAuthService';
import msalAuthService from '../../services/authentication/msal-auth-service/MsalAuthService';
import { StorageKeys } from '../../services/storage/StorageKeys';
import { useQueryNotifications } from '../../queries/UseQueryNotifications';
import { useSaveOfflineConfig } from '../../queries/offline-config/UseSaveOfflineConfig';
import { useOfflineConfig } from '../../queries/offline-config/UseOfflineConfig';
import { OfflineConfigSettings } from '../../services/offline-data-service/OfflineDataService';
import { useOfflineData } from '../../queries/offline-config/UseOfflineData';
import { useOfflineDetectContext } from '../../contexts/offline-detect-context/UseOfflineDetectContext';
import { OfflineDetectActionType } from '../../contexts/offline-detect-context/OfflineDetectContextTypes';
import { useConfirmation } from '../../contexts/confirmation-context/ConfirmationContext';
import { DisableOfflineModeIcon, EnableOfflineModeIcon, UserAccountIcon } from '../action-menu/ActionMenuIcons';
import SideSheet from '../side-sheet/side-sheet/SideSheet';
import { format } from 'date-fns';
import { DATE_TIME_FORMAT_STRING } from '../../common/utilities/TimeframeUtilities';
import AccountProfileV2 from '../account-profile-v2/AccountProfileV2';
import AccountSettingsV2 from '../account-settings-v2/AccountSettingV2';
import CustomerDataService from '../../services/customer-data-service/CustomerDataService';
import { useUICustomizationSettingsDialogState } from '../ui-customization-settings-dialog/useUICustomizationSettingsDialogState';
import UICustomizationSettingsDialog from '../ui-customization-settings-dialog/UICustomizationSettingsDialog';
import { bindUICustomizationSettingsDialog } from '../ui-customization-settings-dialog/bindUICustomizationSettingsDialog';


const _searchHelpFilesHref = "https://support.elynxtech.com/hc/en-us";
const _contactSupportHref = "https://support.elynxtech.com/hc/en-us/requests/new";
const _submitProductIdeaHref = "https://support.elynxtech.com/hc/en-us/community/topics/115000546908-Feature-Requests";
const _recentUpdatesHref = "https://support.elynxtech.com/hc/en-us/categories/360000723614-Release-Notes";
const _termsOfServiceHref = "https://www.elynxtech.com/terms-and-conditions";
const _privacyPolicyHref = "https://www.iubenda.com/privacy-policy/70098978";
const _hardwareOrderHref = "https://support.elynxtech.com/hc/en-us/articles/216690648-Hardware-Order-Form";




export default function AppHeaderActionMenu() {

    const [customerCount, setCustomerCount] = useState<number>();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [groupedActions, setGroupedActions] = useState<IKeyValuePair<string, ActionMenuItem[]>[]>();
    //const [loadingOfflineData, setLoadingOfflineData] = useState<boolean>(false);    
    const [cobrowseCode, setCobrowseCode] = useState<string>();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [accountOpen, setAccountOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);

    const isPhone = isMobile && !isTablet;
    const [appSettings] = useAppSettings();    
    const [userState, dispatchUserState] = useUserContext();

    const { acknowledge } = useConfirmation();

    const [offlineState, dispatchOfflineState] = useOfflineDetectContext();
    const configurationDialog = useUICustomizationSettingsDialogState();

    const { data: offlineConfig } = useQueryNotifications(useOfflineConfig)({});
    const { mutate: saveOfflineConfig } = useQueryNotifications(useSaveOfflineConfig)({});
    const {refetch: refreshOfflineData} = useQueryNotifications(useOfflineData)({
        customerID: userState.customer?.id as number,
        enabled: false
    });    
    

    const { t } = useTranslation();
    
    const open = Boolean(anchorEl);

    const navigateTo = useNavigateTo();


    // const theme = useMemo(() => {
    //     return isPhone ? createTheme(appSettings.themeSet[appSettings.themeSet.mode].appHeaderV2) :
    //         createTheme(appSettings.themeSet[appSettings.themeSet.mode].main);
    // }, [
    //     appSettings.themeSet,
    //     isPhone]
    // );

    const mainTheme = useMemo(
        () => {
            return createTheme(appSettings.themeSet[appSettings.themeSet.mode].main);
        },
        [appSettings.themeSet]
    );

    const appVersion = appSettingsJson.appVersion;

    const handleCobrowseMenuClick = async () => {

        const CobrowseIO: any = (window as any).CobrowseIO;

        await CobrowseIO.client();

        CobrowseIO.start({
            register: false
        });

        const code = await CobrowseIO.createSessionCode();

        setCobrowseCode(code);

        console.log(`remote session code is: ${code}`)
    }

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setDialogOpen(true);
    };

    function onClose() {

        setAnchorEl(null);
        setDialogOpen(false);
    };

    const onHelpMenuClick = (href: string) => {

        setTimeout(() =>
            window.open(href), 0);
    };


    function onUserProfile() {
        setProfileOpen(true);
    }
    function onAccountSettings() {

        setAccountOpen(true);
    }

    function onChangeCustomer() {

        storageService.session.set(LoginChangeCustomerStorageKey, true);

        const url = `${routesByViewID.get(UIViewEnum.Login)?.fullPath}`;
        navigateTo(url, { replace: true });        
    }

    function onSignout() {

        eLynxAuthService.logout();

        const isSSO = UserDataService.getIsSSO();
        if (isSSO) {
            msalAuthService.signOut();
        }

        storageService.session.set(RedirectFromLogoutStorageKey, true);
        storageService.local.remove(StorageKeys.IsSSO);

        dispatchUserState({
            type: UserStateActionTypeEnum.ResetUserState,
        });

        setTimeout(() => {

            storageService.local.remove(StorageKeys.LastCustomerID);
            storageService.session.remove(StorageKeys.LastCustomerID);
            storageService.local.remove(StorageKeys.RedirectRestorePath);

            navigateTo(routesByViewID.get(UIViewEnum.Login)?.fullPath as string, { replace: true });
        }, 500);


    }

    function onToggleOfflineMode() {

          saveOfflineConfig({
                            ...offlineConfig,
                            enabled: !offlineConfig?.enabled
                        } as OfflineConfigSettings);
        
    }

    async function onRefreshOfflineData() {

        //setLoadingOfflineData(true);

        await refreshOfflineData();

        dispatchOfflineState({
            type: OfflineDetectActionType.OfflineDataLoaded,
            payload: true
        });

        acknowledge(t('profile.offlineDataRefreshed' ));
        
        //setLoadingOfflineData(false);
        
    }

    function onMenuVisibilityClick() {
        configurationDialog.open({
            userID: userState.user?.id as number
        });
    }

    function onExecuteAction(item: ActionMenuItem) {
        switch(item.action) {
            case BaseActionEnum.MyProfile:
                onUserProfile();
                break;
                
            case BaseActionEnum.AppPreferences:
                onAccountSettings();
                break;

            case BaseActionEnum.MenuVisibility:
                onMenuVisibilityClick();
                break;

            case BaseActionEnum.ChangeCustomer:
                onChangeCustomer();
                break;

            case BaseActionEnum.Signout:
                onSignout();
                break;

            case BaseActionEnum.ContactSupport:
                onHelpMenuClick(_contactSupportHref);
                break;

            case BaseActionEnum.OfflineMode:
                onToggleOfflineMode();
                break;

            case BaseActionEnum.RefreshOfflineData:
                onRefreshOfflineData();
                break;

            case BaseActionEnum.SearchHelp:
                onHelpMenuClick(_searchHelpFilesHref);
                break;

            case BaseActionEnum.GetRemoteSupportCode:
                handleCobrowseMenuClick();
                return;

            case BaseActionEnum.RecentUpdates:
                onHelpMenuClick(_recentUpdatesHref);
                break;

            case BaseActionEnum.SubmitProductIdea:
                onHelpMenuClick(_submitProductIdeaHref);
                break;

            case BaseActionEnum.HardwareOrder:
                onHelpMenuClick(_hardwareOrderHref);
                break;

            case BaseActionEnum.Terms:
                onHelpMenuClick(_termsOfServiceHref);
                break;

            case BaseActionEnum.PrivacyPolicy:
                onHelpMenuClick(_privacyPolicyHref);                    
                break;


            default:
                break;
            
        }

        onClose();
    }

    function renderGroupItems() {
        return [
            groupedActions?.map((o, ondx) => [
                <ListSubheader key={ondx} 
                    sx={{
                        fontSize: '1.15rem',
                        lineHeight: '24px',
                        fontWeight: 600
                    }}>
                    {o.Key}
                </ListSubheader>

                ,o.Value.map((item, index) => (
                    [
                    <MenuItem
                        key={`mi${index}`}
                        disabled={item.disabled}
                        sx={{
                            textDecoration: item.disabled ? 'line-through' : undefined,
                            marginTop: item.action === BaseActionEnum.Version ? '-8px' : undefined,
                            zIndex: item.action === BaseActionEnum.Version ? 1 : undefined

                        }}
                        onClick={() => onExecuteAction(item)}
                        >
                        {item.action !== BaseActionEnum.Version && 
                        <ListItemIcon 
                            sx={{ 
                                minWidth: theme =>theme.spacing(4) 
                            }}
                        >
                            {
                                item.icon ? item.checked && item.checkedIcon ? 
                                <item.checkedIcon /> : 
                                <item.icon /> :
                                undefined
                            }
                        </ListItemIcon>}
                        <Stack sx={{
                            display: 'inline-flex'
                        }}>

                            {item.label.length > 0 && <Typography variant='inherit'>
                                {item.label}
                            </Typography>}
                            {item.helpText &&
                            <Typography variant='caption'>
                                {item.helpText}
                            </Typography>

                            }
                        </Stack>
                    </MenuItem>,
                    item.dividerAfter && <Divider/>
                    ]
                ))
                ,
                ondx < groupedActions.length - 1 ? <Divider key={`dV${ondx}`}/> : null
            ])
        ];
    }

    function renderList() {
        return (
            <MenuList dense={true}>
                {renderGroupItems()}
            </MenuList>
        );
    }

    function renderMenu() {
        return (
            <ThemeProvider theme={mainTheme}>
                <Menu
                    id='user-menu'
                    color='textPrimary'
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center'
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal:'center'
                    }}
                    keepMounted
                    anchorEl={anchorEl}
                    open={open || dialogOpen}
                    onClose={onClose}
            >

                {renderGroupItems()}

            </Menu>
        </ThemeProvider>
        );
    }

    useEffect(() => {
        const getCustomers = async () => {
            const c = await CustomerDataService.getCustomerList();
            setCustomerCount(c.length);
        }

        getCustomers();

    },[
        setCustomerCount]
    );


    useEffect(() => {

        if (customerCount !== undefined) {

            let actions = _.cloneDeep(appHeaderActionItems);
            if (!isPhone) {
                actions = actions.filter(o => o.action !== BaseActionEnum.OfflineMode &&
                    o.action !== BaseActionEnum.LastRefresh &&
                    o.action !== BaseActionEnum.LastSync
                );
            }

            if (offlineState.offline) {
                actions = actions.filter(o => o.action !== BaseActionEnum.AppPreferences);
            }

            if (customerCount < 2) {
                actions = actions.filter(o => o.action !== BaseActionEnum.ChangeCustomer);
            }

            const versionAction = actions.find(o => o.action === BaseActionEnum.Version);
            if (versionAction) {
                versionAction.label = '';
                versionAction.helpText = t('actions.version', {version: appVersion}).toString();
            }

            const remoteSupportAction = actions.find(o => o.action === BaseActionEnum.GetRemoteSupportCode);
            if (remoteSupportAction) {
                remoteSupportAction.helpText = cobrowseCode;
            }

            const offlineModeAction = actions.find(o => o.action === BaseActionEnum.OfflineMode);
            if (offlineModeAction) {
                offlineModeAction.label = offlineConfig?.enabled ? 
                        t('actions.disableOffineMode') : 
                        t('actions.enableOfflineMode');
                offlineModeAction.icon = offlineConfig?.enabled ? EnableOfflineModeIcon : DisableOfflineModeIcon;
            }

            const lastRefreshAction = actions.find(o => o.action === BaseActionEnum.LastRefresh);
            if (lastRefreshAction) {
                lastRefreshAction.helpText = offlineConfig?.lastRefresh ? format(new Date(offlineConfig?.lastRefresh), DATE_TIME_FORMAT_STRING) : "";
            }

            const lastSyncAction = actions.find(o => o.action === BaseActionEnum.LastSync);
            if (lastSyncAction) {
                lastSyncAction.helpText = offlineConfig?.lastSync ? format(new Date(offlineConfig?.lastSync), DATE_TIME_FORMAT_STRING) : "";
            }


            const newActions = Enumerable.from(actions)
                .groupBy(o => o.category ?? ActionMenuItemCategoryEnum.NONE)
                .select(o => {
        
                    const key = o.key().toString();
        
                    return {
                        //Key: o.key() !== undefined ? ActionMenuItemCategoryEnum[key] : '',
                        Key: key,
                        Value: o.toArray().sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        
                    } as IKeyValuePair<string, ActionMenuItem[]>
                })
                .toArray();        

            setGroupedActions(newActions);

        }


    }, [
        setGroupedActions,
        offlineConfig?.enabled,
        offlineConfig?.lastRefresh,
        offlineConfig?.lastSync,
        t,
        appVersion,
        cobrowseCode,
        isPhone,
        offlineState.offline,
        customerCount
    ]);


    return (
        <Box>


        {isPhone ? 
        <IconButton 
            onClick={handleClick}
            color='inherit'
        >
            {UserAccountIcon()}
        
        </IconButton>

        :    

        <IconButton 
            id="user-menu-button"
            aria-controls={open ? 'user-profile-menu' : undefined}
            aria-haspopup="true"
            onClick={handleClick}
            color='inherit'
        >
            {UserAccountIcon()}
        
        </IconButton>
        }

        {renderMenu()}


        {
        isPhone && 
        <SideSheet
            open={dialogOpen} 
            title=''
            onClose={onClose}
        >
            {renderList()}
        </SideSheet>
        }

        <AccountSettingsV2 open={accountOpen} onClose={() => setAccountOpen(false)} />
        <AccountProfileV2 open={profileOpen} onClose={() => setProfileOpen(false)} />
        <UICustomizationSettingsDialog {...bindUICustomizationSettingsDialog(configurationDialog)} />

    </Box>
    );
}
